import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { useQueryClient } from '@tanstack/react-query';
import { ChefHat, Eye, EyeOff, AlertCircle, Loader2, ArrowLeft, CheckCircle } from 'lucide-react';
import { authApi } from '../services/api';
import { isNativeApp } from '../lib/platform';

export default function LoginPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await authApi.login(email, password);
      const { accessToken, refreshToken, user } = response.data.data;

      // Store tokens
      localStorage.setItem('manager_token', accessToken);
      // Shopify App Store install: the OAuth token is parked server-side;
      // claim it for this account, then land on the integrations card.
      const claimShop = sessionStorage.getItem('pending_shopify_claim') || new URLSearchParams(window.location.search).get('shopifyShop');
      if (claimShop) {
        sessionStorage.removeItem('pending_shopify_claim');
        try {
          await api.post('/v1/production/shopify/claim-install', { shop: claimShop });
          window.location.href = '/settings?tab=integrations&shopify=connected';
          return;
        } catch { /* fall through — they can connect from Settings */ }
      }
      const pendingShop = sessionStorage.getItem('pending_shopify_shop');
      if (pendingShop) {
        sessionStorage.removeItem('pending_shopify_shop');
        try {
          const r = await api.get(`/v1/production/shopify/oauth/start?shop=${encodeURIComponent(pendingShop)}`);
          const u = (r as any).data?.data?.url;
          if (u) { window.location.href = u; return; }
        } catch { /* fall through to normal login landing */ }
      }
      localStorage.setItem('manager_refresh_token', refreshToken);
      localStorage.setItem('manager_user', JSON.stringify(user));

      // Check if user needs onboarding and store restaurants
      const meResponse = await authApi.me();
      const restaurants = meResponse.data.data.restaurants || [];
      // The displayed store MUST match the one the token is scoped to, or the
      // nav shows one location while the data (invoices, P&L, dashboard) is
      // another's. The token's restaurant is user.restaurantId — match it;
      // only fall back to the first when it isn't in the list.
      const tokenRestaurantId = (user as any)?.restaurantId || meResponse.data.data?.user?.restaurantId;
      const currentRestaurant = restaurants.find((r: any) => r.id === tokenRestaurantId) || restaurants[0];

      // Store restaurants list and current restaurant
      localStorage.setItem('manager_restaurants', JSON.stringify(restaurants));
      localStorage.setItem('manager_current_restaurant', JSON.stringify(currentRestaurant));

      // Drop every cached query from any previous session in this browser —
      // otherwise the last account's modules/dashboard keep showing for minutes
      // after switching accounts (wrong nav, wrong home, wrong numbers).
      queryClient.clear();

      if (currentRestaurant?.status === 'onboarding') {
        navigate('/onboarding');
      } else {
        navigate('/');
      }
    } catch (err: any) {
      // Distinguish a real credential rejection (server responded) from a
      // connectivity failure (no response — wrong API URL / offline / CORS).
      // Previously both showed "Invalid email or password", which hid the real
      // cause when the app couldn't reach the API.
      let message: string;
      if (err.response) {
        message = err.response.data?.error?.message || 'Invalid email or password';
      } else {
        message = 'Cannot reach the server. Check your connection and try again.';
      }
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setForgotLoading(true);

    try {
      await authApi.forgotPassword(forgotEmail);
      setForgotSuccess(true);
    } catch (err: any) {
      // Always show success message for security (prevent email enumeration)
      setForgotSuccess(true);
    } finally {
      setForgotLoading(false);
    }
  };

  const resetForgotForm = () => {
    setShowForgotPassword(false);
    setForgotEmail('');
    setForgotSuccess(false);
    setError('');
  };

  return (
    // Scrollable + 1px-taller inner wrapper = iOS rubber-band bounce even
    // though the content fits the screen
    <div className="h-[100dvh] overflow-y-auto overscroll-contain bg-gradient-to-br from-primary-600 to-primary-800">
      <div className="min-h-[calc(100%+1px)] flex items-center justify-center p-4 safe-area-inset-top safe-area-inset-bottom">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <ChefHat className="w-10 h-10 text-white" />
            <img src="/mise-wordmark.svg" alt="mise" className="h-7 invert" />
          </div>
          <p className="text-primary-200 mt-1">Restaurant Operating System</p>
        </div>

        {new URLSearchParams(window.location.search).get('session') === 'expired' && (
          <div className="mb-4 px-4 py-3 rounded-xl bg-white/10 text-white text-sm text-center">
            Your session ended — please sign in again to keep going.
          </div>
        )}

        {/* Login/Forgot Password Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {showForgotPassword ? (
            // Forgot Password Form
            <>
              <button
                onClick={resetForgotForm}
                className="flex items-center gap-1 text-slate-500 hover:text-slate-700 mb-4 text-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to login
              </button>

              {forgotSuccess ? (
                <div className="text-center py-4">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-success-100 rounded-full mb-4">
                    <CheckCircle className="w-6 h-6 text-success-600" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-900 mb-2">Check your email</h2>
                  <p className="text-slate-500 mb-6">
                    If an account exists for {forgotEmail}, you'll receive a password reset link shortly.
                  </p>
                  <button
                    onClick={resetForgotForm}
                    className="btn btn-primary"
                  >
                    Return to login
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-slate-900 text-center mb-2">
                    Reset password
                  </h2>
                  <p className="text-slate-500 text-center mb-6">
                    Enter your email and we'll send you a reset link
                  </p>

                  <form onSubmit={handleForgotPassword} className="space-y-5">
                    <div>
                      <label
                        htmlFor="forgot-email"
                        className="block text-sm font-medium text-slate-700 mb-1.5"
                      >
                        Email address
                      </label>
                      <input
                        id="forgot-email"
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        className="input w-full"
                        placeholder="you@restaurant.com"
                        required
                        autoComplete="email"
                        autoFocus
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={forgotLoading}
                      className="btn btn-primary w-full flex items-center justify-center gap-2"
                    >
                      {forgotLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        'Send reset link'
                      )}
                    </button>
                  </form>
                </>
              )}
            </>
          ) : (
            // Login Form
            <>
              <h2 className="text-2xl font-bold text-slate-900 text-center mb-2">
                Welcome back
              </h2>
              <p className="text-slate-500 text-center mb-6">
                Sign in to your manager dashboard
              </p>

              {/* Error Alert */}
              {error && (
                <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-danger-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-danger-700">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Email */}
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-slate-700 mb-1.5"
                  >
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input w-full"
                    placeholder="you@restaurant.com"
                    required
                    autoComplete="email"
                    autoFocus
                  />
                </div>

                {/* Password */}
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-slate-700 mb-1.5"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="input w-full pr-10"
                      placeholder="Enter your password"
                      required
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Forgot Password */}
                <div className="flex items-center justify-end">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Forgot password?
                  </button>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn btn-primary w-full flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Sign in'
                  )}
                </button>
              </form>

              {/* Create Account Link — web only. The native app is login-only
                  per App Store Guideline 3.1.1 (no in-app business registration). */}
              {!isNativeApp && (
                <div className="mt-6 text-center">
                  <p className="text-slate-500">
                    Don't have an account?{' '}
                    <Link
                      to="/register"
                      className="text-primary-600 hover:text-primary-700 font-medium"
                    >
                      Create account
                    </Link>
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-primary-200 text-sm">
            <a href="https://lamise.us/terms" target="_blank" rel="noopener noreferrer" className="hover:text-white">Terms</a>
            {' · '}
            <a href="https://lamise.us/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-white">Privacy</a>
          </p>
        </div>
      </div>
      </div>
    </div>
  );
}
